/**
 * SESSION-LOGOUT-WHILE-POLLING-01 / H3 -- "connection lost, retrying".
 *
 * A refresh that fails for a TRANSPORT reason (Railway restart, cold start,
 * 502, timeout, dropped Wi-Fi) no longer signs the user out: the tokens are
 * kept and the refresh is retried with backoff. But silence would be its own
 * bug -- a user watching a multi-hour take-off would see a screen that had
 * simply stopped updating, with no way to tell a stalled job from a stalled
 * connection. This says which it is.
 *
 * Rendered by IdleTimerProvider, which already wraps the authenticated app, so
 * no new mount point in Layout/App is needed. Reads lib/connectionState, which
 * lib/api.ts publishes into.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WifiOff } from 'lucide-react'
import {
  getConnectionState,
  subscribeConnectionState,
  type ConnectionState,
} from '@/lib/connectionState'

export default function ConnectionLostBanner() {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState<ConnectionState>(() => getConnectionState())

  useEffect(() => {
    // Re-read on mount as well as subscribing: a refresh may already have
    // failed before this component existed (the boot refresh in
    // onRehydrateStorage runs at module scope).
    setState(getConnectionState())
    return subscribeConnectionState(setState)
  }, [])

  if (state.status !== 'reconnecting') return null

  const isArabic = !i18n.language?.toLowerCase().startsWith('en')

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="connection-lost-banner"
      dir={isArabic ? 'rtl' : 'ltr'}
      className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2
                 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm
                 text-amber-900 shadow-lg
                 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {t('session.connectionLost', {
          defaultValue: isArabic
            ? 'انقطع الاتصال — بنحاول نرجّعه. شغلك محفوظ.'
            : 'Connection lost — retrying. Your work is safe.',
        })}
      </span>
    </div>
  )
}
